/**
 * @desc The 'useBreakpoint()' hook is used to get the current
 *       screen breakpoint based on the TailwindCSS config.
 *
 * @usage
 *    import { useBreakpoint } from "@/hooks/useBreakpoint";
 *
 *    const { isAboveSm, isBelowSm, sm } = useBreakpoint("sm", true);
 *    console.log({ isAboveSm, isBelowSm, sm });
 *
 *    const { isAboveMd, isBelowMd, md } = useBreakpoint("md", true);
 *    const { isAboveLg, isBelowLg, lg } = useBreakpoint("lg", false);
 *
 */
import { useCallback, useEffect, useState } from 'react';
import { throttle } from 'lodash';

import tailwindConfig from '../../tailwind-presets/jabra-preset';

const breakpoints = tailwindConfig.theme.screens;

export function useBreakpoint<K extends string>(breakpointKey: K, watchForChanges?: boolean) {
  type KeyAbove = `isAbove${Capitalize<K>}`;
  type KeyBelow = `isBelow${Capitalize<K>}`;

  const capitalizedKey = breakpointKey[0].toUpperCase() + breakpointKey.substring(1);

  const breakpointValue = breakpoints?.[breakpointKey as keyof typeof breakpoints] || '';

  const [breakpointKeys, setBreakpointKeys] = useState<
    Record<K, number> & Record<KeyAbove | KeyBelow, boolean>
  >({
    [breakpointKey]: Number(String(breakpointValue).replace(/[^0-9]/g, '')),
    [`isAbove${capitalizedKey}`]: false,
    [`isBelow${capitalizedKey}`]: false,
  } as Record<K, number> & Record<KeyAbove | KeyBelow, boolean>);

  const updateBreakpointStatus = useCallback(() => {
    const width = window.innerWidth;
    const breakpointPx = Number(String(breakpointValue).replace('px', ''));

    setBreakpointKeys({
      [breakpointKey]: breakpointPx,
      [`isAbove${capitalizedKey}`]: width >= breakpointPx,
      [`isBelow${capitalizedKey}`]: width < breakpointPx,
    } as Record<K, number> & Record<KeyAbove | KeyBelow, boolean>);
  }, [breakpointKey, breakpointValue, capitalizedKey]);

  useEffect(() => {
    updateBreakpointStatus();
    const throttledUpdateBreakpointStatus = throttle(updateBreakpointStatus, 200);

    if (watchForChanges) {
      window.addEventListener('resize', throttledUpdateBreakpointStatus);
    }

    return () => {
      window.removeEventListener('resize', throttledUpdateBreakpointStatus);
      throttledUpdateBreakpointStatus.cancel();
    };
  }, [updateBreakpointStatus, watchForChanges]);

  return breakpointKeys;
}
